import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Play } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1766756499755-0ae763777974?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxzdGFkaXVtJTIwbGlnaHRzJTIwbmlnaHQlMjBldmVudCUyMGF0bW9zcGhlcmV8ZW58MHx8fHwxNzcxMzcyNjk2fDA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Stadium atmosphere"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#020617] via-[#020617]/80 to-transparent" />
      </div>

      {/* Noise Overlay */}
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      {/* Content */}
      <div className="section-container relative z-10 pt-32 pb-20">
        <div className="max-w-4xl">
          {/* Label */}
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="label-accent mb-6 block"
          >
            Cayman Islands Premier Consultancy
          </motion.span>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold uppercase tracking-tight text-white leading-[0.9] mb-8"
          >
            Elevate Your
            <br />
            <span className="text-[#E53935]">Sports & Business</span>
            <br />
            Strategy
          </motion.h1>

          {/* Sub-headline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-slate-300 max-w-2xl mb-10 leading-relaxed"
          >
            Strategic consulting, sports program development, and brand positioning 
            that delivers measurable results. Transform your vision into championship-level success.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="btn-primary flex items-center justify-center gap-2"
            >
              Book a Consultation
              <ChevronRight className="w-5 h-5" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-secondary"
              className="btn-secondary flex items-center justify-center gap-2"
            >
              <Play className="w-5 h-5" />
              Explore Services
            </Link>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-3 gap-8 mt-16 pt-16 border-t border-[#1E293B]"
          >
            <div>
              <div className="font-heading text-4xl md:text-5xl font-bold text-white">20+</div>
              <div className="text-slate-400 text-sm mt-1">Years Experience</div>
            </div>
            <div>
              <div className="font-heading text-4xl md:text-5xl font-bold text-white">50+</div>
              <div className="text-slate-400 text-sm mt-1">Projects Delivered</div>
            </div>
            <div>
              <div className="font-heading text-4xl md:text-5xl font-bold text-[#E53935]">100%</div>
              <div className="text-slate-400 text-sm mt-1">Client Satisfaction</div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 rounded-full border-2 border-slate-600 flex items-start justify-center p-2">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1 h-2 bg-[#E53935] rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
